import { type CSSProperties } from "react";
import { CheckCircle2, ChevronRight, Loader2, XCircle } from "lucide-react";
import { ShimmerText } from "~/components/assistant/ShimmerText";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { cn } from "~/lib/utils";
import { ToolCallCard } from "./ToolCallCard";
import { isToolError } from "./toolError";
import { useToolCatalog } from "./useToolCatalog";
import type { ToolCallEntry } from "./useTurns";

interface ToolCallItemProps {
  entry: ToolCallEntry;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// A single tool call in the disclosure's vertical list: a collapsible header
// (tool name + pending/done status) wrapping the Input | Progress | Output
// tabs. Each item toggles independently; the parent collapses prior items as
// new tool calls stream in.
export const ToolCallItem = ({ entry, open, onOpenChange }: ToolCallItemProps) => {
  const { resolveTool } = useToolCatalog();
  const pending = entry.result === undefined;
  const failed = !pending && isToolError(entry.result);
  const { displayName: label, toolkitDisplayName } = resolveTool(entry.name);

  return (
    <Collapsible
      open={open}
      onOpenChange={onOpenChange}
      className="overflow-hidden rounded-lg border border-border bg-background shadow-sm"
    >
      <CollapsibleTrigger
        className={cn(
          "group flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-xs text-foreground",
          "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        )}
      >
        <ChevronRight
          className="size-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-90"
          aria-hidden
        />
        {pending ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" aria-hidden />
        ) : failed ? (
          <XCircle className="size-3.5 shrink-0 text-destructive" aria-hidden />
        ) : (
          <CheckCircle2 className="size-3.5 shrink-0 text-emerald-600" aria-hidden />
        )}
        <div className="flex min-w-0 items-baseline">
          {pending ? (
            <ShimmerText
              className="truncate text-xs font-medium"
              style={
                {
                  "--base-color": "var(--muted-foreground)",
                  "--shimmer-color": "var(--foreground)",
                } as CSSProperties
              }
            >
              {label}
            </ShimmerText>
          ) : (
            <span className="truncate text-xs font-medium">{label}</span>
          )}
          {/* Toolkit provenance rides alongside the tool name but only unveils
              once the item is open — it slides/fades in from zero width so a
              collapsed row stays flush against the name. */}
          {toolkitDisplayName && (
            <span
              aria-hidden={!open}
              className={cn(
                "shrink-0 truncate text-xs font-normal text-muted-foreground transition-all duration-200 ease-out",
                open ? "ml-1.5 max-w-[16rem] opacity-70" : "ml-0 max-w-0 opacity-0"
              )}
            >
              from {toolkitDisplayName}
            </span>
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent animated>
        <div className="border-t border-border/60 bg-muted/30">
          <ToolCallCard entry={entry} />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
